import { useRef, useCallback, useEffect } from "react";

/**
 * Off by default: browser FFT cannot reliably separate one candidate + interviewer TTS
 * from two humans; produces constant false "multiple_voices" flags.
 */
export const MULTIPLE_VOICE_DETECTION_ENABLED = false;

// Vocal band for speech (Hz)
const VOCAL_LOW = 85;
const VOCAL_HIGH = 3000;

const ANALYSIS_INTERVAL = 2000;
const ENERGY_THRESHOLD = 0.022;
const CONSECUTIVE_REQUIRED = 5;
const ALERT_COOLDOWN = 30000;

/**
 * Group spectral peaks into fundamentals (harmonics of one speaker share one fundamental).
 * Returns distinct voice fundamentals with meaningful energy.
 */
function countDistinctVoiceFundamentals(peaks) {
  if (!peaks.length) return 0;

  const sorted = [...peaks].sort((a, b) => a.freq - b.freq);
  const groups = [];

  for (const peak of sorted) {
    let placed = false;
    for (const group of groups) {
      const f0 = group.fundamental.freq;
      const ratio = peak.freq / f0;
      const harmonic = Math.round(ratio);
      if (
        harmonic >= 1 &&
        harmonic <= 8 &&
        Math.abs(ratio - harmonic) <= 0.08 + harmonic * 0.04
      ) {
        group.peaks.push(peak);
        group.maxEnergy = Math.max(group.maxEnergy, peak.energy);
        placed = true;
        break;
      }
    }
    if (!placed) {
      groups.push({
        fundamental: peak,
        peaks: [peak],
        maxEnergy: peak.energy,
      });
    }
  }

  if (!groups.length) return 0;

  const loudest = Math.max(...groups.map((g) => g.maxEnergy));
  return groups.filter((g) => g.maxEnergy >= loudest * 0.45).length;
}

export function useVoiceAnalysis({ onViolation, enabled = true }) {
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const consecutiveRef = useRef(0);
  const lastAlertRef = useRef(0);
  const runningRef = useRef(false);

  const getFrequencyIndex = useCallback((freq, sampleRate, fftSize) => {
    return Math.round((freq / (sampleRate / 2)) * (fftSize / 2));
  }, []);

  const findVocalPeaks = useCallback((dataArray, sampleRate, fftSize) => {
    const lowIdx = getFrequencyIndex(VOCAL_LOW, sampleRate, fftSize);
    const highIdx = getFrequencyIndex(VOCAL_HIGH, sampleRate, fftSize);

    let bandEnergy = 0;
    let bandBins = 0;
    for (let i = lowIdx; i < highIdx; i++) {
      bandEnergy += dataArray[i] / 255;
      bandBins += 1;
    }
    const avgBandEnergy = bandBins ? bandEnergy / bandBins : 0;
    if (avgBandEnergy < ENERGY_THRESHOLD * 0.5) {
      return { peaks: [], avgBandEnergy, voiceCount: 0 };
    }

    const peaks = [];
    for (let i = lowIdx + 1; i < highIdx - 1; i++) {
      const val = dataArray[i] / 255;
      if (
        val > ENERGY_THRESHOLD &&
        val > dataArray[i - 1] / 255 &&
        val > dataArray[i + 1] / 255
      ) {
        const freqHz = (i / (fftSize / 2)) * (sampleRate / 2);
        peaks.push({ energy: val, freq: freqHz });
      }
    }

    peaks.sort((a, b) => b.energy - a.energy);
    const voiceCount = countDistinctVoiceFundamentals(peaks.slice(0, 12));

    return { peaks, avgBandEnergy, voiceCount };
  }, [getFrequencyIndex]);

  const analyze = useCallback(() => {
    if (!MULTIPLE_VOICE_DETECTION_ENABLED || !analyserRef.current || !enabled) return;

    const analyser = analyserRef.current;
    const fftSize = analyser.fftSize;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);

    const sampleRate = audioCtxRef.current.sampleRate;
    const { peaks, voiceCount } = findVocalPeaks(dataArray, sampleRate, fftSize);

    if (voiceCount >= 2) {
      consecutiveRef.current += 1;
    } else {
      consecutiveRef.current = Math.max(0, consecutiveRef.current - 2);
    }

    if (
      consecutiveRef.current >= CONSECUTIVE_REQUIRED &&
      Date.now() - lastAlertRef.current > ALERT_COOLDOWN
    ) {
      lastAlertRef.current = Date.now();
      consecutiveRef.current = 0;

      onViolation?.({
        type: "multiple_voices",
        severity: "high",
        message: "Multiple voices detected in audio",
        details: {
          voice_fundamentals: voiceCount,
          peaks_analyzed: peaks.length,
        },
      });
    }
  }, [enabled, findVocalPeaks, onViolation]);

  const start = useCallback(async (existingStream = null) => {
    if (!MULTIPLE_VOICE_DETECTION_ENABLED || runningRef.current || !enabled) return;

    try {
      let stream = existingStream;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        });
        streamRef.current = stream;
      }

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.85;

      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);

      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      sourceRef.current = source;
      runningRef.current = true;

      intervalRef.current = setInterval(analyze, ANALYSIS_INTERVAL);
      console.log("✅ Voice analysis started (mic-only, harmonic grouping)");
    } catch (e) {
      console.warn("⚠️ Voice analysis failed to start:", e);
    }
  }, [enabled, analyze]);

  const stop = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;

    sourceRef.current?.disconnect();
    audioCtxRef.current?.close();

    streamRef.current?.getTracks().forEach((t) => t.stop());

    audioCtxRef.current = null;
    analyserRef.current = null;
    sourceRef.current = null;
    streamRef.current = null;
    runningRef.current = false;
    consecutiveRef.current = 0;
    lastAlertRef.current = 0;

    console.log("🛑 Voice analysis stopped");
  }, []);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return { start, stop };
}
